import axios from "axios";
import {
  Selector,
  Input,
  Modal,
  Button,
  ToggleButton,
  FormRow,
  FormColumn,
  FormHeader,
  Item,
  ItemList,
  Error,
} from "globalComponents";
import { days, getTimezones, states } from "globalResources/constants";
import { errorHandler, getClientPermissions } from "globalResources/util";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { getTheme } from "Theme";
const theme = getTheme();

const Main = styled.div`
  display: flex;
  flex-direction: column;
  width: 800px;
`;

const Title = styled.h2`
  margin: 0 0 10px 0;
`;

// Invite / Create switch for a new user
const ModeSwitch = styled.div`
  display: flex;
  margin-bottom: 16px;
  border: 2px solid ${theme.MAIN_COLOR};
  border-radius: 8px;
  overflow: hidden;
  width: fit-content;
`;

const ModeOption = styled.button`
  border: none;
  padding: 8px 18px;
  font-weight: 700;
  cursor: pointer;
  background: ${(props) => (props.active ? theme.MAIN_COLOR : "white")};
  color: ${(props) => (props.active ? "white" : theme.MAIN_COLOR)};
`;

const Help = styled.p`
  opacity: 0.75;
  margin: 4px 0 12px 0;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;
`;

const SectionTitle = styled.h4`
  margin: 0;
`;

const CodeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const Code = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 2px;
`;

const SendRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  > :first-child {
    flex: 1;
  }
`;

const Sent = styled.div`
  color: ${theme.SUCCESS};
  font-weight: 600;
`;

const defaultForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  language: "english",
  accountType: "employee",
  employeeType: "W2",
  pin: "",
  subLunch: "",
  clientPermissions: ["reports", "approve", "timeentries"],
  smsClockinDisabled: false,
  state: "",
  zipcode: null,
  addressLine1: "",
  addressLine2: "",
  workweekStartDay: null,
};

const defaultErrors = {
  ...defaultForm,
  language: "",
  accountType: "",
  employeeType: "",
  clientPermissions: "",
  overrideLunch: false,
  externalId: null,
  accountingTimezone: "US/Central",
  state: "",
  zipcode: null,
  addressLine1: "",
  addressLine2: "",
  workweekStartDay: "",
};

const UserModal = ({ onClose, selected }) => {
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState(defaultErrors);
  const [loading, setLoading] = useState(false);
  // a new user is invited (they sign themselves up) or created here
  const [mode, setMode] = useState("invite");
  const [inviteInfo, setInviteInfo] = useState(null);
  const [contact, setContact] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [copied, setCopied] = useState("");
  const isNew = !selected?._id;

  useEffect(() => {
    if (!selected) return;
    if (selected._id) setForm({ ...selected });
    else setForm(defaultForm);
    setMode("invite");
    setContact("");
    setInviteError("");
    setSentTo("");
    setCopied("");
  }, [selected]);

  // the company code and sign-up link to share
  useEffect(() => {
    if (!selected || selected._id || inviteInfo) return;
    axios
      .get("/users/invite")
      .then((res) => setInviteInfo(res.data))
      .catch(async (err) => {
        const { message } = await errorHandler(err);
        setInviteError(message || "Couldn't load the company code");
      });
  }, [selected, inviteInfo]);

  const copy = async (what, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
    } catch {
      // clipboard blocked (http, permissions): show it to copy by hand
      window.prompt("Copy this:", text);
    }
  };

  const sendInvite = async () => {
    if (loading) return;
    if (!contact.trim())
      return setInviteError("Enter an email address or phone number");
    setLoading(true);
    setInviteError("");
    setSentTo("");
    try {
      const res = await axios.post("/users/invite", { contact });
      setSentTo(`Sent by ${res.data.via} to ${res.data.invited}`);
      setContact("");
    } catch (err) {
      const { message } = await errorHandler(err);
      // the API's own message says what's wrong (errorHandler makes every
      // 400 a generic "Provided values are invalid")
      const apiMessage = err.response?.data;
      setInviteError(
        (typeof apiMessage === "string" && apiMessage) ||
          message ||
          "Couldn't send the invite",
      );
    } finally {
      setLoading(false);
    }
  };


  const user = useSelector((state) => state.current.user);

  const getAccountTypeOptions = () => {
    let options = [
      { label: "Employee", value: "employee" },
      { label: "Client", value: "client" },
    ];

    if (user.accountType === "super")
      return options.concat([
        { label: "Owner", value: "super" },
        { label: "Admin", value: "admin" },
      ]);
    else if (user.accountType === "admin")
      return options.concat([{ label: "Admin", value: "admin" }]);
    else return options;
  };

  const updateForm = (value, name, isDelete) => {
    setForm((old) => ({ ...old, [name]: value }));
    setErrors((old) => ({ ...old, [name]: "" }));
  };

  const saveUser = () => {
    setLoading(true);
    axios[selected._id ? "patch" : "post"](
      `${process.env.REACT_APP_API_URL}/users/`,
      {
        ...form,
        id: selected._id,
      },
    )
      .then((res) => {
        setForm(defaultForm);
        setErrors(defaultErrors);
        onClose();
      })
      .catch(async (err) => {
        const { message } = await errorHandler(err);
        setErrors((old) => ({ ...old, general: message }));
      })
      .finally(() => setLoading(false));
  };

  const toggleDisable = () => {
    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/user/archive`, {
        id: selected._id,
      })
      .then((res) => {
        setForm(defaultForm);
        setErrors(defaultErrors);
        onClose();
      })
      .catch(async (error) => {
        let { message } = await errorHandler(error);
        setErrors((old) => ({
          ...old,
          general: "Error archiving user: " + message,
        }));
      })
      .finally(() => setLoading(false));
  };

  const getByValue = (value, list) => {
    return list.find((item) => item.value === value);
  };

  if (!selected) return null;

  const modeSwitch = isNew && (
    <ModeSwitch>
      <ModeOption
        type="button"
        data-testid="mode-invite"
        active={mode === "invite"}
        onClick={() => setMode("invite")}
      >
        Invite
      </ModeOption>
      <ModeOption
        type="button"
        data-testid="mode-create"
        active={mode === "create"}
        onClick={() => setMode("create")}
      >
        Create
      </ModeOption>
    </ModeSwitch>
  );

  if (isNew && mode === "invite")
    return (
      <Modal onClose={onClose}>
        <Main>
          <Title>Add User</Title>
          {modeSwitch}
          <Help>
            Share your company code. People enter it after they sign up (or in
            the mobile app), and you accept them on the Company page,
            where you choose their account type and groups.
          </Help>
          <Section>
            <SectionTitle>Company code</SectionTitle>
            <CodeRow>
              <Code data-testid="invite-code">
                {inviteInfo?.joinCode || "----"}
              </Code>
              <Button
                onClick={() => copy("code", inviteInfo.joinCode)}
                disabled={!inviteInfo}
              >
                {copied === "code" ? "Copied" : "Copy Code"}
              </Button>
              <Button
                color={theme.BUTTON_COLOR_GREY}
                onClick={() =>
                  copy(
                    "message",
                    `Join ${inviteInfo.company} on TCS: sign up at ${inviteInfo.signupUrl} and use company code ${inviteInfo.joinCode}`,
                  )
                }
                disabled={!inviteInfo}
              >
                {copied === "message" ? "Copied" : "Copy Invite Message"}
              </Button>
            </CodeRow>
          </Section>
          <Section>
            <SectionTitle>Or send it for me</SectionTitle>
            <SendRow>
              <Input
                label="Email or Phone Number"
                name="inviteContact"
                value={contact}
                onChange={(e) => {
                  setContact(e.currentTarget.value);
                  setInviteError("");
                  setSentTo("");
                }}
                onEnter={sendInvite}
                error={inviteError}
              />
              <Button disabled={loading} onClick={sendInvite}>
                {loading ? "Sending..." : "Send Invite"}
              </Button>
            </SendRow>
            {sentTo && <Sent data-testid="invite-sent">{sentTo}</Sent>}
          </Section>
          <FormRow>
            <Button onClick={onClose}>Done</Button>
          </FormRow>
        </Main>
      </Modal>
    );

  return (
    <Modal onClose={onClose}>
      <Main>
        <Title>{isNew ? "Add User" : "Edit User"}</Title>
        {modeSwitch}
        <FormRow>
          <Input
            label="First Name"
            value={form.firstName}
            onChange={(e) => updateForm(e.currentTarget.value, "firstName")}
            error={errors.firstName}
          />
          <Input
            label="Last Name"
            value={form.lastName}
            onChange={(e) => updateForm(e.currentTarget.value, "lastName")}
            error={errors.lastName}
          />
        </FormRow>
        <FormRow>
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => updateForm(e.currentTarget.value, "email")}
            error={errors.email}
          />
          <Input
            label="Phone Number"
            type="phone"
            value={form.phone}
            onChange={(e) => updateForm(e.currentTarget.value, "phone")}
            error={errors.phone}
          />
        </FormRow>
        <FormRow>
          <Selector
            label="Language"
            options={[
              { label: "English", value: "english" },
              { label: "Spanish", value: "spanish" },
            ]}
            value={form.language}
            onSelect={(v) => updateForm(v, "language")}
            error={errors.language}
          />
          <Selector
            label="Account Type"
            options={getAccountTypeOptions()}
            value={form.accountType}
            onSelect={(v) => updateForm(v, "accountType")}
            error={errors.accountType}
          />
        </FormRow>

        {form.accountType !== "client" && (
          <>
            <FormRow>
              <Selector
                label="Employment Type"
                value={form.employeeType}
                onSelect={(v) => updateForm(v, "employeeType")}
                error={errors.employeeType}
                options={[
                  { label: "W2", value: "W2" },
                  { label: "1099", value: "1099" },
                ]}
              />
              <Input
                label="Timestation Pin Number"
                type="number"
                value={form.pin}
                onChange={(e) => updateForm(e.currentTarget.value, "pin")}
                error={errors.pin}
              />
            </FormRow>
            <FormRow>
              <Input
                label="Address Line 1"
                type="string"
                value={form.addressLine1}
                onChange={(e) =>
                  updateForm(e.currentTarget.value, "addressLine1")
                }
                error={errors.addressLine1}
              />{" "}
              <Selector
                label="Work week Start Day"
                width="300px"
                options={days.map((day) =>
                  day.value === "none" ? { ...day, label: "Default" } : day,
                )}
                value={form.workweekStartDay}
                onSelect={(v) => updateForm(v, "workweekStartDay")}
              />
            </FormRow>
            <FormRow>
              <Input
                label="Address Line 2"
                type="string"
                value={form.addressLine2}
                onChange={(e) =>
                  updateForm(e.currentTarget.value, "addressLine2")
                }
                error={errors.addressLine2}
              />
              <Selector
                label="State"
                width="300px"
                options={states}
                value={form.state}
                onSelect={(v) => updateForm(v, "state")}
              />
              <Input
                label="Zipcode"
                type="number"
                value={form.zipcode}
                onChange={(e) => updateForm(e.currentTarget.value, "zipcode")}
                error={errors.zipcode}
              />
            </FormRow>
            <FormRow>
              <Input
                label="External ID"
                type="number"
                value={form.externalId}
                onChange={(e) =>
                  updateForm(e.currentTarget.value, "externalId")
                }
                error={errors.externalId}
              />{" "}
              <Selector
                label="Accounting Timezone"
                value={form.accountingTimezone}
                onSelect={(v) => updateForm(v, "accountingTimezone")}
                error={errors.accountingTimezone}
                options={getTimezones()}
              />
            </FormRow>
          </>
        )}

        <FormRow>
          {form.accountType === "client" ? (
            <FormColumn>
              <FormHeader>Client Permissions</FormHeader>
              <ItemList>
                {getClientPermissions().map((permission) => (
                  <Item>
                    <ToggleButton
                      value={form.clientPermissions.includes(permission.value)}
                      onChange={() =>
                        updateForm(
                          !form.clientPermissions.includes(permission.value)
                            ? form.clientPermissions.concat([permission.value])
                            : form.clientPermissions.filter(
                                (p) => p !== permission.value,
                              ),
                          "clientPermissions",
                        )
                      }
                    />
                    {permission.label}
                  </Item>
                ))}
              </ItemList>
            </FormColumn>
          ) : (
            <FormColumn>
              <ToggleButton
                label="Enable Mobile Clock-In"
                onChange={() =>
                  updateForm(!form.smsClockinDisabled, "smsClockinDisabled")
                }
                value={!form.smsClockinDisabled}
              />
            </FormColumn>
          )}
          <FormColumn>
            <ToggleButton
              label="Override Lunch Subtraction"
              onChange={() => updateForm(!form.overrideLunch, "overrideLunch")}
              value={form.overrideLunch}
            />
          </FormColumn>
        </FormRow>
        <Error>{errors.general}</Error>
        <FormRow>
          {!isNew && (
            <Button onClick={toggleDisable} color={theme.BUTTON_COLOR_GREY}>
              {selected.isArchived ? "Enable" : "Disable"} User
            </Button>
          )}
          <Button onClick={saveUser}>
            {isNew ? "Create User" : "Save Changes"}
          </Button>
        </FormRow>
      </Main>
    </Modal>
  );
};
export default UserModal;
